import React from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import Header from './Header';
import SEO from './SEO';
import Footer from './Footer';
import { useNavigate } from 'react-router-dom';

const articles = [
  {
    slug: '/blog/arizona-coolidge-daycare-lawsuit',
    tag: 'Audio Detection',
    readTime: '6 min read',
    title: 'She Threatened to Lock Them in a Dark Room. One Parent\'s Recording Caught It. AI Would Have Caught It First.',
    excerpt: 'Parents of 19 children in Coolidge, Arizona sued after audio recordings revealed a caregiver threatening children with a "dark room." A parent captured it on her phone. Here\'s the threat pattern audio AI detects in real time — before 19 families need a lawyer.',
    color: '#8c52ff',
  },
  {
    slug: '/blog/sexual-predators-daycare-prevention',
    tag: 'Child Safety',
    readTime: '8 min read',
    title: 'The Risk No Background Check Closes — And What Behavioral AI Can Do About It',
    excerpt: 'In 2024, 27 states were failing federal background check requirements for childcare workers. Even perfect checks miss first-time offenders. Real cases from 2024–2026 show how abuse is discovered — almost never by any safety system.',
    color: '#ff6b6b',
  },
  {
    slug: '/blog/disputed-allegations-rainbow-childcare',
    tag: 'Trust & Transparency',
    readTime: '6 min read',
    title: '"It Never Happened." When a Daycare and a Parent Disagree, Only One Thing Settles It.',
    excerpt: 'A mother said her child left a Hilliard, Ohio daycare with six fractured bones. The owner said it never happened — and that cameras were running. The mother said the cameras were down. 12,000 shares later, there was still no footage. This is what SMCLA solves for both sides.',
    color: '#5ce1e6',
  },
  {
    slug: '/blog/rocking-horse-ranch-harvey-mucklebust',
    tag: 'Behavioral AI',
    readTime: '7 min read',
    title: 'Three Incidents. One Worker. Three Weeks. How Behavioral AI Detects the Pattern Cameras Alone Miss.',
    excerpt: 'Harvey Muklebust was 11 months old when he died at a Savage, Minnesota daycare on September 22, 2025. The worker who killed him had been hired three weeks earlier — and had already harmed a 4-month-old twice. The pattern existed. No system was watching for it.',
    color: '#5ce1e6',
  },
  {
    slug: '/blog/suny-cortland-audio-detection',
    tag: 'Audio Detection',
    readTime: '7 min read',
    title: '"No, Don\'t. It Hurts." — What Audio Would Have Caught at the SUNY Cortland Daycare.',
    excerpt: 'Four workers — including the executive director — were arrested for abusing children at a university-run daycare in 2026. The recordings captured a child saying "no, don\'t, it hurts." No system was listening. Here\'s what audio AI would have flagged from week one.',
    color: '#8c52ff',
  },
  {
    slug: '/blog/des-plaines-mothers-touch',
    tag: 'Case Study',
    readTime: '6 min read',
    title: "15 Charges, 7 Boys, 16 Days. What the Cameras at \"A Mother's Touch\" Already Had.",
    excerpt: "A parent reported water being sprayed at a child. That complaint — not any safety system — is what made the Des Plaines daycare finally review its own footage. What they found covered 16 days and seven children.",
    color: '#ff6b6b',
  },
  {
    slug: '/blog/punkin-doodles-134-incidents',
    tag: 'Case Study',
    readTime: '8 min read',
    title: '134 Incidents. Three Months. Six Toddlers. How AI Would Have Stopped It on Day One.',
    excerpt: 'A daycare worker in Lindale, Texas kicked six 2-year-olds 134 times over three months. Cameras recorded every incident. Not one was flagged until a coworker stumbled across the footage by chance.',
    color: '#ff9f43',
  },
  {
    slug: '/blog/cost-of-one-bad-hire',
    tag: 'Financial Risk',
    readTime: '7 min read',
    title: 'The True Cost of One Bad Hire in Childcare: From Day 1 to Day 365',
    excerpt: 'A single abusive employee costs far more than their salary. We break down the real financial spiral — legal fees, settlements, enrollment loss, and what it takes to rebuild.',
    color: '#ff6b6b',
  },
  {
    slug: '/blog/cameras-not-enough',
    tag: 'Safety Technology',
    readTime: '5 min read',
    title: 'Why Security Cameras Aren\'t Enough to Protect Your Daycare (And What Is)',
    excerpt: 'Bright Horizons had cameras. Three workers abused nine children for 18 months. Here\'s why cameras record abuse — and why AI stops it.',
    color: '#5ce1e6',
  },
  {
    slug: '/blog/how-to-prevent-daycare-abuse',
    tag: 'Prevention Guide',
    readTime: '8 min read',
    title: '5 Proven Ways to Prevent Abuse at Your Daycare (Including One No One Talks About)',
    excerpt: 'From policy to AI: a practical guide to the five layers of protection that actually work, with data from 2024–2025 abuse cases showing what failed and what would have helped.',
    color: '#8c52ff',
  },
  {
    slug: '/blog/emotional-abuse-detection',
    tag: 'Emotional Safety',
    readTime: '6 min read',
    title: 'The Abuse Cameras Can\'t See: How AI Detects Emotional Abuse in Daycares',
    excerpt: 'Shouting, humiliation, verbal threats — emotional abuse leaves no bruises but causes lasting harm. Here\'s how audio AI detection changes what\'s possible.',
    color: '#ff9f43',
  },
];

export default function BlogPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="Childcare Safety Blog — Case Studies & Insights" description="In-depth analysis of real childcare abuse cases, behavioral AI research, and safety best practices for daycare operators and insurers." path="/blog" />
      <Header onBookDemo={() => navigate('/contact')} />

      <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
          Resources & Insights
        </Typography>
        <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2rem', md: '3rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 2 }}>
          The SMCLA Blog
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 18, color: '#9499b0', mb: 8, lineHeight: 1.65, maxWidth: 500 }}>
          Practical guides, case studies, and data-driven insights for daycare operators who take safety seriously.
        </Typography>

        <Grid container spacing={3}>
          {articles.map(({ slug, tag, readTime, title, excerpt, color }) => (
            <Grid key={slug} size={{ xs: 12 }}>
              <Box
                onClick={() => navigate(slug)}
                sx={{ p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { borderColor: 'rgba(92,225,230,0.3)', transform: 'translateY(-2px)', boxShadow: '0 12px 32px rgba(0,0,0,0.3)' } }}
              >
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ px: 1.5, py: 0.5, bgcolor: `${color}15`, border: `1px solid ${color}40`, borderRadius: 1 }}>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      {tag}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#9499b0', display: 'flex', alignItems: 'center' }}>
                    {readTime}
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: { xs: 18, md: 22 }, color: '#f0f0f8', mb: 1.5, lineHeight: 1.3 }}>
                  {title}
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7, mb: 2 }}>
                  {excerpt}
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 13, color: '#5ce1e6' }}>
                  Read article →
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 8, p: 4, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 3, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 18, color: '#f0f0f8', mb: 1 }}>
            Ready to protect your facility?
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', mb: 3 }}>
            Book a 30-minute personalized demo and see the AI in action.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/contact')} sx={{ px: 4, py: 1.25, fontWeight: 700, fontSize: 15, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, '&:hover': { bgcolor: '#4ecfd4' } }}>
            Book a Demo →
          </Button>
        </Box>
      </Container>

      <Footer />
    </Box>
  );
}
